import { IContractCruMetric } from "dto/Contracts/IContractCru";
import { axiosInstance } from "./axios";
import { ItemKind } from "dto/IKindItems";
import { IEncryptedGenericResponse } from "dto/IEncryptedGenericResponse";
import { IPackage } from "dto/IPackage";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { EParcelType } from "dto/EParcelType";
import { IHybridSettings } from "dto/IHybridSettings";
import { EEntityType } from "dto/EEntityType";
import { IEntity } from "dto/IEntity";
import { IInitOutgoingParcelResponse } from "dto/IInitOutgoingParcelResponse";
import {
  ICourtPortalWritingAttachmentType,
  ICourtPortalWritingType,
} from "dto/CourtPortal/ICourtPortalWritingType";
import {
  EParcelAccess,
  IParcelLookup,
  IParcelReceiver,
  IParcelStore,
  IRegistryOffice,
} from "dto/Mailroom/IMailroom";
import {
  IConfirmPickupRequest,
  IPickupInfo,
} from "dto/Mailroom/IPickup";

const queryKeys = {
  initParcel: () => "parcelService.initParcel",
  searchAdeForEntity: () => "parcelService.searchAdeForEntity",
  getCourtPortalWritingTypes: () => "parcelService.getCourtPortalWritingTypes",
  getCourtPortalWritingAttachmentTypes: () =>
    "parcelService.getCourtPortalWritingAttachmentTypes",
};

export interface Attachment {
  Order: number;
  Uri: string;

  // Biuro podawcze / kreator rejestracji
  TypeId?: number | null;
  Title?: string | null;
  Signature?: string | null;
  IsDigital?: boolean;
  DigitalMapping?: boolean;
  Number?: number | null;
  DamageDescriptor?: string | null;
}

export interface ICreateParcel {
  Attachments: Attachment[];
  CaseId: string | null;
  ReceiveDate: string;
  ReceiverId: string | null;
  SenderId: number | null;
  Title: string | null;
  TagIds?: string[];

  // Biuro podawcze / kreator rejestracji
  DeliveryKindId?: number | null;
  RegistryOfficeId?: string | null;
  ParcelStoreId?: string | null;
  CarrierSignature?: string | null;
  Access?: EParcelAccess;
  IsDamaged?: boolean;
  DamageDescription?: string | null;
  PassedOnWithoutOpening?: boolean;

  /** Przekazanie do innego użytkownika po zarejestrowaniu (dekretacja). */
  PassToUserId?: string | null;

  PublicKey?: string;
  PrivateKey?: string;

  /** Metryka CRU głównego załącznika rodzaju „Umowa" (walidacja odroczona). */
  ContractCruMetric?: IContractCruMetric | null;
}

const createParcel = (
  payload: ICreateParcel,
): Promise<{ data: IEncryptedGenericResponse<IPackage[]> }> => {
  return axiosInstance.post("/incomingParcel", payload);
};

const useCreateParcel = () => {
  return useCustomMutation(createParcel);
};

export interface IResponseInit {
  Receivers: IParcelReceiver[];
  RegistryOfficeEmployee?: boolean;

  // Słowniki i domyślne ustawienia kreatora (biuro podawcze)
  AttachmentTypes?: IParcelLookup[];
  DeliveryKinds?: IParcelLookup[];
  RegistryOffices?: IRegistryOffice[];
  ParcelStores?: IParcelStore[];
  DefaultReceiverId?: string | null;
  DefaultRegistryOfficeId?: string | null;
  DefaultParcelStoreId?: string | null;
}

const initParcel = (
  currentWorkspaceId?: string,
): Promise<{ data: IResponseInit }> => {
  const q = currentWorkspaceId
    ? `?currentWorkspaceId=${currentWorkspaceId}`
    : "";
  return axiosInstance.get(`incomingParcel/init${q}`);
};

const useInitParcel = (currentWorkspaceId?: string) => {
  return useCustomQuery([queryKeys.initParcel(), currentWorkspaceId], () =>
    initParcel(currentWorkspaceId),
  );
};

export interface IOutgoingParcelRelation {
  Id: string;
  Kind: ItemKind;
}

export interface IRequestCreateOutGoingParcel {
  PublicKey: string;
  PrivateKey: string;
  OriginalPrivateKey: string;
  DocumentId: string | null;
  CaseId: string | null;
  Title: string;
  Content: string;
  RawContent: string | null;
  Attachments: { Encrypted: string; Order: number; Uri: string }[];
  Receivers: Receiver[];
  Cc: Receiver[];
  Reply?: {
    Id: string;
    Kind: string;
  };
  Forward?: {
    Id: string;
    Kind: string;
  };

  // Biuro podawcze (obieg pism wychodzących)
  /** Przesyłki idą przez biuro podawcze: wstrzymane + numeracja XXX.n + status obiegu. */
  ViaMailroom?: boolean;
  /** Skład wychodzący nadający sygnaturę (dla ViaMailroom). */
  ParcelStoreId?: string | null;
  /** Data pisma (dla ViaMailroom). */
  DocumentDate?: string | null;
  /** Ścieżka analogowa (papier) — nie wymaga podpisanej wersji PDF. */
  Analog?: boolean;
  /**
   * Kopie per odbiorca są JUŻ podpisane elektronicznie (własny podpis — każdy
   * wygenerowany PDF podpisany przez wtyczkę przy tych samych współrzędnych pola).
   * BE Create pomija wtedy wymóg podpisanej wersji ŹRÓDŁA (bramka signedVersion)
   * i przyjmuje podpisane URI odbiorców as-is. Obieg pozostaje ReadyToDispatch
   * (przesyłki idą do biura podawczego, jak przy podpisie tradycyjnym).
   */
  ReceiversPreSigned?: boolean;
  /** Ustawienia PUH (hybrydowa) — wypełniane gdy wśród odbiorców jest kanał Puh. */
  HybridSettings?: IHybridSettings;
}

export interface Receiver {
  Parcel: EParcelType;
  /** Forma doręczenia ze słownika ParcelDeliveryKinds (List polecony / ZPO / osobiste ...). */
  DeliveryKindId?: number | null;
  Id?: string | null;
  Type?: EEntityType;
  Email: string | undefined;
  EDeliveryAddress?: string;
  /** URI wygenerowanego przez wtyczkę, sparametryzowanego PDF pisma dla tego odbiorcy (zachowany oryginał). */
  Uri?: string | null;
}

const createOutGoingParcel = (
  payload: IRequestCreateOutGoingParcel,
): Promise<{ data: IEncryptedGenericResponse<IPackage[]> }> => {
  return axiosInstance.post("/outgoingParcel", payload);
};

const useCreateOutGoingParcel = () => {
  return useCustomMutation(createOutGoingParcel);
};

export interface IDispatchParcelRequest {
  /** Grupa pism (GroupId) — nadanie całej wysyłki naraz. */
  GroupId?: string | null;
  /** Alternatywnie: konkretne przesyłki. */
  ParcelIds?: string[];
  /** Numer nadawczy (papier). */
  DispatchNumber?: string | null;
  /** Data nadania (papier). */
  DispatchDate?: string | null;
  /** Przewoźnik / operator (papier). */
  Carrier?: string | null;
}

const dispatchParcel = (
  payload: IDispatchParcelRequest,
): Promise<{ data: unknown }> => {
  return axiosInstance.post("/outgoingParcel/dispatch", payload);
};

const useDispatchParcel = () => {
  return useCustomMutation(dispatchParcel);
};

// ── Zadanie 2: zmiana sposobu nadania (forma doręczenia) przesyłki wychodzącej ──

export interface IChangeDeliveryKindRequest {
  ParcelId: string;
  /** Nowa forma doręczenia ze słownika ParcelDeliveryKinds. */
  DeliveryKindId: number;
}

const changeDeliveryKind = (
  payload: IChangeDeliveryKindRequest,
): Promise<{ data: unknown }> => {
  return axiosInstance.post("/outgoingParcel/changeDeliveryKind", payload);
};

const useChangeDeliveryKind = () => {
  return useCustomMutation(changeDeliveryKind);
};

// ── Zadanie 1: potwierdzenie odbioru osobistego przesyłki WYCHODZĄCEJ ───────────
// (forma doręczenia „Odbiór osobisty" = PersonalPickup; adresat odbiera osobiście).

const confirmOutgoingPickup = (
  payload: IConfirmPickupRequest,
): Promise<{ data: unknown }> => {
  return axiosInstance.post("/outgoingParcel/confirmPickup", payload);
};

const useConfirmOutgoingPickup = () => {
  return useCustomMutation(confirmOutgoingPickup);
};

const getOutgoingPickupInfo = (
  parcelId: string,
): Promise<{ data: IPickupInfo }> => {
  return axiosInstance.get(`outgoingParcel/pickup/${parcelId}`);
};

const useGetOutgoingPickupInfo = (parcelId: string) => {
  return useCustomQuery(
    ["outgoingParcel.pickup", parcelId],
    () => getOutgoingPickupInfo(parcelId),
    { enabled: !!parcelId },
  );
};

/**
 * Weryfikuje odbiorcę i zwraca pełen kontakt z BE
 * @param payload - potencjalny odbiorca przesyłki
 * @returns 200 w przypadku znalezienia
 * 404 gdy nie znaleziono kontaktu
 * 400 gdy coś nie tak z requestem
 */
const verifyReceiver = (payload: Receiver): Promise<{ data: IEntity }> => {
  return axiosInstance.post("/outgoingParcel/verifyReceiver", payload);
};

const useVerifyReceiver = () => {
  return useCustomMutation(verifyReceiver);
};

const getOutcomingInit = (): Promise<{
  data: IInitOutgoingParcelResponse;
}> => {
  return axiosInstance.get("outgoingParcel/init");
};

const useGetOutcomingInit = () => {
  return useCustomQuery("outcommingParcel", () => getOutcomingInit());
};

const searchAdeForEntity = (id: string): Promise<{ data: IEntity }> => {
  return axiosInstance.get(`addressBook/checkAde?id=${id}`);
};

const useSearchAdeForEntity = () => {
  return useCustomMutation(searchAdeForEntity);
};

const getCourtPortalWritingTypes = (): Promise<{
  data: ICourtPortalWritingType[];
}> => {
  return axiosInstance.get("courtPortal/writing/types");
};

const useGetCourtPortalWritingTypes = () => {
  return useCustomQuery("getCourtPortalWritingTypes", () =>
    getCourtPortalWritingTypes(),
  );
};

const getCourtPortalWritingAttachmentTypes = (): Promise<{
  data: ICourtPortalWritingAttachmentType[];
}> => {
  return axiosInstance.get("courtPortal/writing/attachment/types");
};

const useGetCourtPortalWritingAttachmentTypes = () => {
  return useCustomQuery("getCourtPortalWritingAttachmentTypes", () =>
    getCourtPortalWritingAttachmentTypes(),
  );
};

export default {
  useCreateParcel,
  useInitParcel,
  useCreateOutGoingParcel,
  useDispatchParcel,
  useChangeDeliveryKind,
  useConfirmOutgoingPickup,
  useGetOutgoingPickupInfo,
  useGetOutcomingInit,
  useVerifyReceiver,
  useSearchAdeForEntity,
  useGetCourtPortalWritingTypes,
  useGetCourtPortalWritingAttachmentTypes,
};
